<template>
  <BuildingDetailsSkeleton v-if="status === 'pending'" />

  <View v-else-if="building" direction="column" gap="lg">
    <View justify="space-between" align="center" gap="md" wrap="wrap">
      <View gap="sm" align="center" wrap="wrap">
        <Text variant="h2">Building details</Text>
        <Badge v-if="building.portfolio" icon="folder" :label="building.portfolio.name" variant="ghost" size="small" />
      </View>
      <BuildingMenuButton :building="building" />
    </View>

    <BuildingPhotos :images="building.images" @edit="photosModalRef?.open()" />
    <BuildingPhotosModal ref="photosModalRef" :building="building" />

    <!-- What every unit shares: set once here, pushed to each unit's listing -->
    <DetailsSectionHeader
      title="Shared by every unit"
      description="Changes here show on all units immediately — address, amenities, parking and access live on the building, not the unit."
    />
    <DetailsGrid>
      <BuildingIdentitySection :building="building" />
      <BuildingLocationSection :building="building" />
      <BuildingAmenitiesSection :building="building" />
      <BuildingParkingSection :building="building" />
      <BuildingAccessibilitySection :building="building" class="full-size" />
    </DetailsGrid>

    <!-- What a new unit starts with: copied into it when it is added, then the unit's own -->
    <DetailsSectionHeader
      title="Defaults for new units"
      description="Copied into a unit when it's created. After that each unit owns its copy — editing here changes nothing on existing units."
    />
    <DetailsGrid>
      <BuildingPetPolicySection :building="building" :units="units" />
      <BuildingRenterRequirementsSection :building="building" :units="units" />
      <BuildingLeaseTermSection :building="building" :units="units" />
      <BuildingDepositsFeesSection :building="building" :units="units" />
      <BuildingListingSettingsSection :building="building" :units="units" />
    </DetailsGrid>
  </View>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRoute } from 'vue-router';

import { Badge, Text, View } from '@tallo/design-system';
import { useBuildingQuery, useBuildingUnitsQuery } from '@tallo/investor/property';
import { DetailsGrid, DetailsSectionHeader } from '@tallo/property';

import BuildingMenuButton from '../../components/building-menu-button.vue';
import BuildingPhotosModal from '../../components/building-photos-modal.vue';
import BuildingPhotos from '../../components/building-photos.vue';
import BuildingDetailsSkeleton from './building-details-skeleton.vue';
import BuildingAccessibilitySection from './sections/building-accessibility-section.vue';
import BuildingAmenitiesSection from './sections/building-amenities-section.vue';
import BuildingDepositsFeesSection from './sections/building-deposits-fees-section.vue';
import BuildingIdentitySection from './sections/building-identity-section.vue';
import BuildingLeaseTermSection from './sections/building-lease-term-section.vue';
import BuildingListingSettingsSection from './sections/building-listing-settings-section.vue';
import BuildingLocationSection from './sections/building-location-section.vue';
import BuildingParkingSection from './sections/building-parking-section.vue';
import BuildingPetPolicySection from './sections/building-pet-policy-section.vue';
import BuildingRenterRequirementsSection from './sections/building-renter-requirements-section.vue';

const route = useRoute();
const { data: building, status } = useBuildingQuery(() => route.params.buildingId as string);
const { units } = useBuildingUnitsQuery(() => route.params.buildingId as string);
const photosModalRef = ref<InstanceType<typeof BuildingPhotosModal>>();
</script>
